/**
 * Fetching and decoding the baked waveform payload.
 *
 * The server stores five uint8 arrays back to back — peak, rms, low, mid, high — one
 * byte per bin. This mirrors `decodeWaveform` on the server side; the two must agree
 * on band order, so change them together and bump the format version.
 */

export type WaveformData = {
  bins: number;
  peak: Float32Array;
  rms: Float32Array;
  low: Float32Array;
  mid: Float32Array;
  high: Float32Array;
};

const BANDS = 5;

export function decodeWaveformPayload(bytes: Uint8Array, bins: number): WaveformData {
  const read = (band: number): Float32Array => {
    const values = new Float32Array(bins);
    const offset = band * bins;
    for (let index = 0; index < bins; index += 1) values[index] = (bytes[offset + index] ?? 0) / 255;
    return values;
  };
  return { bins, peak: read(0), rms: read(1), low: read(2), mid: read(3), high: read(4) };
}

/**
 * Load a track's waveform. Resolves to `null` when the bake has not finished — an
 * expected state, since a track becomes playable before its analysis completes.
 */
export async function fetchWaveform(
  trackId: string,
  signal?: AbortSignal,
): Promise<WaveformData | null> {
  const response = await fetch(`/api/tracks/${encodeURIComponent(trackId)}/waveform`, {
    credentials: 'same-origin',
    ...(signal ? { signal } : {}),
  });
  if (response.status === 404) return null;
  if (!response.ok) throw new Error(`Waveform request failed with status ${response.status}`);

  const buffer = new Uint8Array(await response.arrayBuffer());
  const header = Number(response.headers.get('x-waveform-bins'));
  const bins = Number.isFinite(header) && header > 0 ? header : Math.floor(buffer.length / BANDS);
  if (bins <= 0 || buffer.length < bins * BANDS) {
    throw new Error('Waveform payload is malformed');
  }
  return decodeWaveformPayload(buffer, bins);
}
